/**
 * Gateway analytics (Sep 11 2026): which pages a visitor opens on this host,
 * and how far through /checkout they get. The events go to Amplitude's
 * gateway project, never to "P3 - Production" (signed-in students only), and
 * the Data Matrix reads them back as its Enterprise section.
 *
 * WHAT IS SENT, AND WHAT NEVER IS
 *  - An anonymous device id kept in localStorage (no cookie) and no user id,
 *    ever: a signed-in administrator is as anonymous here as a visitor.
 *  - The page's name from the route table; the checkout's stage, path, band,
 *    billing interval and, on a failure, its kind. Never a name, an email, an
 *    institution, a figure from the dashboard, a join code, or anything typed.
 *  - Nothing at all until VITE_AMPLITUDE_GATEWAY_KEY is set at build time.
 *    It is the gateway project's PUBLIC browser key, never a secret key.
 *
 * Amplitude is on the subprocessor list already ("Product analytics"). The
 * Worker's policy admits https://api2.amplitude.com for connect-src, and the
 * SDK's remote configuration is off so it asks for nothing else.
 */

import { createInstance } from "@amplitude/analytics-browser";

import type { Billing, CheckoutPath, FailureKind, ReturnNotice } from "~/app/components/checkout/checkout-model";
import type { Route } from "~/app/router";

export const SURFACE = "enterprise";

/** Every route as an analyst reads it. A new route kind fails the type check until it is named here. */
export const PAGE_NAME: Readonly<Record<Route["kind"], string>> = {
  partner: "Overview",
  pricing: "Pricing",
  checkout: "Checkout",
  "checkout-complete": "Checkout complete",
  trust: "Security",
  login: "Sign in",
  admin: "Dashboard",
  "admin-settings": "Settings",
  "admin-billing": "Billing",
  "admin-team": "Team",
  "admin-roster": "Enrollment",
  "admin-invitations": "Invitations",
  "admin-matching": "Matching",
  demo: "Sample dashboard",
  "demo-settings": "Sample settings",
  "demo-billing": "Sample billing",
  "demo-team": "Sample team",
  "demo-roster": "Sample enrollment",
  "demo-invitations": "Sample invitations",
  "demo-matching": "Sample matching",
  join: "Join",
  "join-done": "Join done",
  "not-found": "Not found",
};

/**
 * The checkout's stages. The three steps are sent as the buyer reaches them,
 * then what the submit did. `returned` is a buyer back from Stripe with a
 * notice (cancelled, expired, declined).
 */
export type CheckoutStage =
  | "institution"
  | "payment"
  | "review"
  | "submitted"
  | "quote_sent"
  | "card_redirect"
  | "failed"
  | "already_subscribed"
  | "returned";

export interface CheckoutContext {
  readonly path: CheckoutPath;
  readonly band: string;
  readonly billing: Billing;
}

interface Tracker {
  track(event: string, properties: Readonly<Record<string, string>>): void;
}

let client: Tracker | null = null;
let settled = false;

/**
 * A missing key is the default state, so reading it must never throw. The
 * app is also rendered where `import.meta.env` is not a Vite object.
 */
function readKey(): string | null {
  try {
    const env = (import.meta as { env?: Record<string, unknown> }).env;
    const key = env?.VITE_AMPLITUDE_GATEWAY_KEY;
    return typeof key === "string" && key.length > 0 ? key : null;
  } catch {
    return null;
  }
}

function tracker(): Tracker | null {
  if (settled) return client;
  settled = true;
  const key = readKey();
  if (!key || typeof window === "undefined") return null;
  try {
    const instance = createInstance();
    instance.init(key, {
      autocapture: {
        sessions: true,
        attribution: true,
        pageViews: false,
        formInteractions: false,
        fileDownloads: false,
        elementInteractions: false,
      },
      identityStorage: "localStorage",
      fetchRemoteConfig: false,
    });
    client = {
      track: (event, properties) => {
        instance.track(event, { ...properties });
      },
    };
  } catch {
    client = null;
  }
  return client;
}

/** Tests put a recorder in place; null puts the built client back. */
export function setTrackerForTests(fake: Tracker | null): void {
  client = fake;
  settled = fake !== null;
}

function send(event: string, properties: Readonly<Record<string, string>>): void {
  const t = tracker();
  if (!t) return;
  try {
    t.track(event, { ...properties, surface: SURFACE });
  } catch {
    // Analytics never breaks a page.
  }
}

export function trackPage(kind: Route["kind"]): void {
  send("page_view", { page: PAGE_NAME[kind] });
}

export function trackCheckout(
  stage: CheckoutStage,
  context: CheckoutContext,
  detail: { readonly failure?: FailureKind | null; readonly notice?: ReturnNotice } = {},
): void {
  const properties: Record<string, string> = {
    stage,
    path: context.path,
    band: context.band,
    billing: context.billing,
  };
  if (detail.failure) properties["failure"] = detail.failure;
  if (detail.notice) properties["notice"] = detail.notice;
  send("checkout_stage", properties);
}
